import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Copy, ExternalLink, BarChart3 } from "lucide-react";

const Q_TYPES = [
  { value: "text", label: "Short text" },
  { value: "long_text", label: "Long text (qualitative)" },
  { value: "rating", label: "Rating (1-5)" },
  { value: "likert", label: "Likert (1-7)" },
  { value: "number", label: "Number" },
  { value: "multiple_choice", label: "Multiple choice (single)" },
  { value: "checkbox", label: "Multiple choice (multi)" },
  { value: "date", label: "Date" },
];

export default function Surveys() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", research_type: "mixed", language: "en",
    anonymous: true, require_unique: false, allow_draft_save: true,
    questions: [{ id: "q1", text: "", type: "text" }],
    pages: [{ title: "Page 1", question_ids: ["q1"] }],
  });

  const load = () => api.get("/surveys").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  // Add a question to the LAST page by default
  const addQ = () => {
    const nextId = `q${form.questions.length + 1}`;
    const newQ = { id: nextId, text: "", type: "text" };
    const newPages = form.pages.length > 0 ? form.pages.map((p, i) =>
      i === form.pages.length - 1 ? { ...p, question_ids: [...(p.question_ids || []), nextId] } : p
    ) : [{ title: "Page 1", question_ids: [nextId] }];
    setForm({ ...form, questions: [...form.questions, newQ], pages: newPages });
  };

  const removeQ = (qid) => {
    setForm({
      ...form,
      questions: form.questions.filter((q) => (q.id || "") !== qid),
      pages: form.pages.map((p) => ({ ...p, question_ids: (p.question_ids || []).filter((x) => x !== qid) })),
    });
  };

  const updateQ = (qid, field, value) => {
    setForm({
      ...form,
      questions: form.questions.map((q) => ((q.id || "") === qid ? { ...q, [field]: value } : q)),
    });
  };

  const addPage = () => {
    setForm({ ...form, pages: [...form.pages, { title: `Page ${form.pages.length + 1}`, question_ids: [] }] });
  };

  const removePage = (idx) => {
    if (form.pages.length <= 1) return;
    // re-attach orphaned questions to previous page
    const dropped = form.pages[idx].question_ids || [];
    const newPages = form.pages.filter((_, i) => i !== idx).map((p, i) =>
      i === Math.max(0, idx - 1) ? { ...p, question_ids: [...(p.question_ids || []), ...dropped] } : p
    );
    setForm({ ...form, pages: newPages });
  };

  const updatePage = (idx, field, value) => {
    setForm({ ...form, pages: form.pages.map((p, i) => (i === idx ? { ...p, [field]: value } : p)) });
  };

  const moveQuestionToPage = (qid, newPageIdx) => {
    setForm({
      ...form,
      pages: form.pages.map((p, i) => ({
        ...p,
        question_ids: i === newPageIdx
          ? [...(p.question_ids || []).filter((x) => x !== qid), qid]
          : (p.question_ids || []).filter((x) => x !== qid),
      })),
    });
  };

  // Find which page a question belongs to
  const pageOfQuestion = (qid) => form.pages.findIndex((p) => (p.question_ids || []).includes(qid));

  const create = async (e) => {
    e.preventDefault();
    const cleanQs = form.questions.filter((q) => q.text.trim()).map((q, i) => ({ ...q, id: q.id || `q${i+1}` }));
    if (cleanQs.length === 0) { toast.error("Add at least one question"); return; }
    // Rebuild pages from current form, dropping question_ids that no longer exist
    const validIds = new Set(cleanQs.map((q) => q.id));
    const cleanPages = form.pages
      .map((p, i) => ({
        title: p.title || `Page ${i + 1}`,
        question_ids: (p.question_ids || []).filter((qid) => validIds.has(qid)),
      }))
      .filter((p) => p.question_ids.length > 0);
    // If somehow no pages, fallback to a single page containing all qs
    const finalPages = cleanPages.length > 0 ? cleanPages : [{ title: "Page 1", question_ids: cleanQs.map((q) => q.id) }];
    setCreating(true);
    try {
      await api.post("/surveys", { ...form, questions: cleanQs, pages: finalPages });
      toast.success("Survey published");
      setForm({
        title: "", description: "", research_type: "mixed", language: "en",
        anonymous: true, require_unique: false, allow_draft_save: true,
        questions: [{ id: "q1", text: "", type: "text" }],
        pages: [{ title: "Page 1", question_ids: ["q1"] }],
      });
      load();
    } catch (e) { toast.error("Failed to create survey"); }
    finally { setCreating(false); }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/survey/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Survey link copied");
  };

  return (
    <div data-testid="surveys-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RESEARCH-GRADE SURVEYS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Build <em className="italic text-kb-primary">rigorous</em> surveys.</h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Quantitative, qualitative or mixed-methods — with conditional logic, anti-fraud monitoring and a built-in analytics dashboard.
      </p>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <form onSubmit={create} className="lg:col-span-7 kb-card p-7" data-testid="survey-create-form">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">NEW SURVEY</div>
          <div className="mt-4 space-y-4">
            <input required name="title" className="kb-input" placeholder="Survey title"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="survey-title-input" />
            <textarea rows={2} className="kb-input resize-none" placeholder="Description shown to respondents…"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="survey-description-input" />

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Research type</label>
                <select className="kb-input mt-1.5" value={form.research_type}
                  onChange={(e) => setForm({ ...form, research_type: e.target.value })}
                  data-testid="survey-research-type">
                  <option value="mixed">Mixed-methods</option>
                  <option value="quantitative">Quantitative</option>
                  <option value="qualitative">Qualitative</option>
                </select>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Language</label>
                <select className="kb-input mt-1.5" value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  data-testid="survey-language">
                  <option value="en">English</option>
                  <option value="ne">नेपाली (Nepali)</option>
                  <option value="bilingual">Bilingual (en + ne)</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Quality</label>
                <label className="text-xs text-kb-ink-muted flex items-center gap-2">
                  <input type="checkbox" checked={form.anonymous}
                    onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                    data-testid="survey-anonymous-checkbox" />
                  Anonymous responses
                </label>
                <label className="text-xs text-kb-ink-muted flex items-center gap-2">
                  <input type="checkbox" checked={form.require_unique}
                    onChange={(e) => setForm({ ...form, require_unique: e.target.checked })}
                    data-testid="survey-unique-checkbox" />
                  Block duplicates (IP/UA fingerprint)
                </label>
                <label className="text-xs text-kb-ink-muted flex items-center gap-2">
                  <input type="checkbox" checked={form.allow_draft_save}
                    onChange={(e) => setForm({ ...form, allow_draft_save: e.target.checked })}
                    data-testid="survey-draft-checkbox" />
                  Allow draft auto-save
                </label>
              </div>
            </div>

            {/* Pages manager */}
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PAGES — {form.pages.length}</div>
                <button type="button" onClick={addPage} className="text-kb-primary text-xs hover:underline inline-flex items-center gap-1" data-testid="survey-add-page-btn">
                  <Plus className="w-3 h-3" /> Add page
                </button>
              </div>
              <div className="space-y-1.5">
                {form.pages.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-2 py-1.5" data-testid={`survey-page-${i}`}>
                    <span className="kb-mono text-[10px] text-kb-primary w-6">P{i + 1}</span>
                    <input className="flex-1 text-sm bg-transparent outline-none border-b border-transparent focus:border-kb-primary"
                      placeholder={`Page ${i + 1} title`}
                      value={p.title || ""} onChange={(e) => updatePage(i, "title", e.target.value)}
                      data-testid={`survey-page-${i}-title`} />
                    <span className="text-[10px] text-kb-ink-light kb-mono">{(p.question_ids || []).length}Q</span>
                    {form.pages.length > 1 && (
                      <button type="button" onClick={() => removePage(i)} className="text-rose-500 hover:text-rose-700" data-testid={`survey-page-${i}-remove`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-kb-ink-light mt-2 leading-relaxed">Use multiple pages for long surveys — respondents see one page at a time with auto-saved progress.</p>
            </div>

            <div className="space-y-3">
              {form.questions.map((q, idx) => {
                const qid = q.id || `q${idx + 1}`;
                const curPage = pageOfQuestion(qid);
                return (
                <div key={qid} className="border border-slate-200 rounded-sm p-3 bg-white" data-testid={`survey-q-${idx}`}>
                  <div className="flex items-center gap-2 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2 flex-wrap">
                    <span className="text-kb-primary">Q{idx + 1}</span>
                    <select className="kb-input py-1 px-2 text-xs" value={q.type}
                      onChange={(e) => updateQ(qid, "type", e.target.value)}
                      data-testid={`survey-q-${idx}-type`}>
                      {Q_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <select className="kb-input py-1 px-2 text-xs" value={curPage === -1 ? 0 : curPage}
                      onChange={(e) => moveQuestionToPage(qid, parseInt(e.target.value))}
                      data-testid={`survey-q-${idx}-page`}>
                      {form.pages.map((_, i) => <option key={i} value={i}>Page {i + 1}</option>)}
                    </select>
                    <input className="kb-input py-1 px-2 text-xs w-44" placeholder="visible_if: q1=YES (optional)"
                      value={q.visible_if || ""} onChange={(e) => updateQ(qid, "visible_if", e.target.value)}
                      data-testid={`survey-q-${idx}-conditional`} />
                    <label className="text-[10px] text-kb-ink-muted flex items-center gap-1 ml-auto">
                      <input type="checkbox" checked={!!q.required}
                        onChange={(e) => updateQ(qid, "required", e.target.checked)} />
                      Required
                    </label>
                    {form.questions.length > 1 && (
                      <button type="button" onClick={() => removeQ(qid)} className="text-kb-ink-light hover:text-rose-600" data-testid={`survey-q-${idx}-remove`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <input required className="kb-input" placeholder="Question text"
                    value={q.text} onChange={(e) => updateQ(qid, "text", e.target.value)}
                    data-testid={`survey-q-${idx}-text`} />
                  {(q.type === "multiple_choice" || q.type === "checkbox") && (
                    <input className="kb-input mt-2" placeholder="Comma separated options"
                      value={q.options || ""} onChange={(e) => updateQ(qid, "options", e.target.value)}
                      data-testid={`survey-q-${idx}-options`} />
                  )}
                </div>
              );})}
              <button type="button" onClick={addQ} className="kb-btn-outline text-sm" data-testid="survey-add-question-btn">
                <Plus className="w-3.5 h-3.5" /> Add question
              </button>
            </div>

            <button type="submit" disabled={creating} className="kb-btn-primary mt-2" data-testid="survey-create-btn">
              {creating ? "Publishing…" : "Publish survey"}
            </button>
          </div>
        </form>

        <div className="lg:col-span-5">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">YOUR SURVEYS</div>
          {items.length === 0 ? (
            <div className="kb-card p-6 text-sm text-kb-ink-muted">No surveys yet.</div>
          ) : (
            <div className="space-y-3">
              {items.map((s) => (
                <div key={s.id} className="kb-card p-5" data-testid={`survey-row-${s.id}`}>
                  <div className="font-serif text-lg text-kb-ink leading-tight">{s.title}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-light mt-1">{s.id} · {s.questions.length} questions · {s.research_type} · {s.language}</div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm">
                      {s.responses_count} responses · {s.completed_count} clean
                    </span>
                    <div className="flex gap-1">
                      <Link to={`/dashboard/surveys/${s.id}/analytics`} className="text-kb-ink-muted hover:text-kb-primary p-2" title="Analytics" data-testid={`survey-${s.id}-analytics`}>
                        <BarChart3 className="w-3.5 h-3.5" />
                      </Link>
                      <button onClick={() => copyLink(s.id)} className="text-kb-ink-muted hover:text-kb-primary p-2" data-testid={`survey-${s.id}-copy`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <a href={`/survey/${s.id}`} target="_blank" rel="noreferrer" className="text-kb-ink-muted hover:text-kb-primary p-2" data-testid={`survey-${s.id}-open`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
